import { html, type Html, Page, rune, View } from 'rune-ts';
import klass from './ReceiptViewer.module.scss';
import { receipt_viewer_path } from './ReceiptViewerRoute';
import { typo } from '../../../shared/typography/typo';
import { CustomerOrder, ReceiptOption, ReceiptViewerType } from './type';
import { makeQueryString } from '../../../../../shared/utils/makeString/makeQueryString';
import { font } from '../../../shared/typography';
import { UtilS } from '../../../../../modules/Util/S/Function/module/UtilS';
import { GoodsTypeS } from '../../../../../modules/GoodsType/S/Function/module/GoodsTypeS';
import {
  PROVIDER,
  SELLER,
} from '../../../../../modules/MShop/App/CustomerOrderReceipt/S/Constant/customerorderreceipt';
import { formatCurrency } from '../../../shared/util/formatCurrency/formatCurrency';
import { makeTopSectionHtml } from './html';
import { PurchaseReceiptData, PurchaseReceiptViewer } from './views/PurchaseReceiptViewer';
import { CardReceiptData, CardReceiptViewer } from './views/CardReceiptViewer';
import { CashReceiptData, CashReceiptViewer } from './views/CashReceiptViewer';
import { loading, loadingEnd } from '../../../shared/client/global';
import { Alert } from '../../../shared/components/cells/Alert/Alert';

export type ReceiptViewerPageData = PurchaseReceiptData | CardReceiptData | CashReceiptData;

export const ReceiptViewerDefaultSize = {
  width: 608,
  height: 720,
};

export class ReceiptViewerPage extends Page<ReceiptViewerPageData> {
  protected override template(): Html {
    return html`${this.getViewByType(this.data)}`;
  }

  private getViewByType(data: ReceiptViewerPageData): View | Html {
    switch (data.type) {
      case ReceiptViewerType.PURCHASE:
        return new PurchaseReceiptViewer(data);
      case ReceiptViewerType.CARD:
        return this.makeBaseReceiptHtml(new CardReceiptViewer(data), data.customer_order, data.option);
      case ReceiptViewerType.CASH:
        return this.makeBaseReceiptHtml(new CashReceiptViewer(data), data.customer_order, data.option);
      default:
        return html`잘못된 요청입니다.`;
    }
  }

  override onRender() {
    const current_height = this.element().scrollHeight + 80;

    if (current_height < ReceiptViewerDefaultSize.height) {
      window.resizeTo(ReceiptViewerDefaultSize.width, current_height);
    } else {
      window.resizeTo(ReceiptViewerDefaultSize.width, ReceiptViewerDefaultSize.height);
    }

    this.delegate('click', `.${klass.button_pdf}`, this.pdfHandler);
    this.delegate('click', `.${klass.button_print}`, this.printHandler);
  }

  async pdfHandler() {
    loading();
    window.scrollTo(0, 0);
    try {
      const clone$ = this.element().cloneNode(true) as HTMLElement;

      // 영수증 정보 행간 조절
      clone$.querySelectorAll<HTMLElement>('.purchase-receipt-customer-order__info').forEach((info$) => {
        info$.style.rowGap = '0.5rem';
      });

      // 버튼 제거
      clone$.querySelector<HTMLElement>(`.${klass.buttons}`)?.remove();

      clone$.style.padding = '2rem';

      const file_name_map = {
        [ReceiptViewerPage.type.CARD]: ET('customer_order::receipt::card'),
        [ReceiptViewerPage.type.CASH]: ET('customer_order::receipt::cash'),
        [ReceiptViewerPage.type.PURCHASE]: ET('customer_order::receipt::purchase'),
      };

      await window.html2pdf(clone$, {
        filename: file_name_map[this.data.type],
        margin: 10,
      });

      loadingEnd();
    } catch (e) {
      loadingEnd();
      await Alert.open({
        title: '다운로드 실패',
        message: '죄송합니다. 다운로드에 문제가 있습니다. 잠시 후 다시 시도해 주세요.',
      });
    }
  }

  async printHandler() {
    loading();
    window.scrollTo(0, 0);

    try {
      // 현재 요소를 복제
      const clone$ = this.element().cloneNode(true) as HTMLElement;
      // svg 요소 제거
      clone$.querySelectorAll('svg').forEach((svg) => {
        svg.parentNode?.removeChild(svg);
      });

      // 영수증 정보 행간 조절
      clone$.querySelectorAll<HTMLElement>('.purchase-receipt-customer-order__info').forEach((info$) => {
        info$.style.rowGap = '0.5rem';
      });

      // 버튼 제거
      clone$.querySelector<HTMLElement>(`.${klass.buttons}`)?.remove();

      // 프린트를 위한 스타일 요소 생성
      const printStyles = document.createElement('style');
      printStyles.textContent = `
      @media print {
        body * {
          visibility: hidden;
        }
        #print-container, #print-container * {
          visibility: visible;
        }
        #print-container {
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          top: 0;
          width: 100%;
          max-width: 800px;
          padding: 2rem;
        }
      }
    `;

      // 임시 컨테이너 생성 및 설정
      const printContainer = document.createElement('div');
      printContainer.id = 'print-container';
      printContainer.style.visibility = 'hidden';
      printContainer.appendChild(clone$);

      // DOM에 임시로 추가
      document.body.appendChild(printStyles);
      document.body.appendChild(printContainer);

      // 프린트 다이얼로그 실행
      window.print();

      // 청소
      document.body.removeChild(printStyles);
      document.body.removeChild(printContainer);

      loadingEnd();
    } catch (e) {
      loadingEnd();
      console.log(e);
      await Alert.open({
        title: '인쇄 실패',
        message: '죄송합니다. 인쇄 준비 중 문제가 발생했습니다. 잠시 후 다시 시도해 주세요.',
      });
    }
  }

  makeBaseReceiptHtml(
    receiptViewer: CardReceiptViewer | CashReceiptViewer,
    customer_order: CustomerOrder,
    option: ReceiptOption,
  ): Html {
    const { impt } = customer_order;
    const seller =
      /* TODO PBG 상품은 일단 생각말자. */
      SELLER[!UtilS.isNessApp() || GoodsTypeS.isMps(option.goods_type_id) ? 'mps' : 'nexon'];
    const provider = PROVIDER[impt.pg_provider as keyof typeof PROVIDER];

    const is_cancel = impt.cancel_amount > 0;
    const cancel_price = is_cancel ? Number(option.price) : 0;
    const result_price = is_cancel ? 0 : Number(option.price);
    const supply_price = Math.round(result_price / 1.1);
    const vat_price = result_price - supply_price;

    return html`
      <div class="${klass.root}">
        ${makeTopSectionHtml(
          receiptViewer instanceof CardReceiptViewer ? this.T.card_title_t : this.T.cash_title_t,
          this.T.pdf_t,
          this.T.print_t,
        )}
        <div class="receipt-area-print-pdf">
          ${impt.cancel_amount > 0
            ? html`
                <div class="purchase-receipt-customer-order__cancel ${typo('14_medium')}">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g clip-path="url(#clip0_1837_233818)">
                      <path
                        d="M9.99935 1.66406C5.39935 1.66406 1.66602 5.3974 1.66602 9.9974C1.66602 14.5974 5.39935 18.3307 9.99935 18.3307C14.5993 18.3307 18.3327 14.5974 18.3327 9.9974C18.3327 5.3974 14.5993 1.66406 9.99935 1.66406ZM11.041 14.0641C11.041 14.6391 10.5743 15.1057 9.99935 15.1057C9.42435 15.1057 8.95768 14.6391 8.95768 14.0641V9.68906C8.95768 9.11406 9.42435 8.6474 9.99935 8.6474C10.5743 8.6474 11.041 9.11406 11.041 9.68906V14.0641ZM9.99935 6.98073C9.42435 6.98073 8.95768 6.51406 8.95768 5.93906C8.95768 5.36406 9.42435 4.8974 9.99935 4.8974C10.5743 4.8974 11.041 5.36406 11.041 5.93906C11.041 6.51406 10.5743 6.98073 9.99935 6.98073Z"
                        fill="#D92A0F"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_1837_233818">
                        <rect width="20" height="20" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>

                  ${this.T.cancel_info_t}
                </div>
              `
            : ''}
          ${receiptViewer}
          <div class="purchase-receipt-customer-order__info ${typo('16_medium')}">
            <div class="purchase-receipt-customer-order__info-title ${typo('16_bold')}">
              ${this.T.seller_info_t}
            </div>
            ${seller
              ? html`
                  <div class="purchase-receipt-customer-order__row">
                    <div class="purchase-receipt-customer-order__info-left">${this.T.seller_name_t}</div>
                    <div class="purchase-receipt-customer-order__info-right">${seller.name}</div>
                  </div>
                  <div class="purchase-receipt-customer-order__row">
                    <div class="purchase-receipt-customer-order__info-left">${this.T.seller_ceo_t}</div>
                    <div class="purchase-receipt-customer-order__info-right">${seller.ceo}</div>
                  </div>
                  <div class="purchase-receipt-customer-order__row">
                    <div class="purchase-receipt-customer-order__info-left">${this.T.seller_num_t}</div>
                    <div class="purchase-receipt-customer-order__info-right">${seller.num}</div>
                  </div>
                  <div class="purchase-receipt-customer-order__row">
                    <div class="purchase-receipt-customer-order__info-left">${this.T.seller_tel_t}</div>
                    <div class="purchase-receipt-customer-order__info-right">${seller.tel}</div>
                  </div>
                  <div class="purchase-receipt-customer-order__row">
                    <div class="purchase-receipt-customer-order__info-left">${this.T.seller_address_t}</div>
                    <div class="purchase-receipt-customer-order__info-right">${seller.address}</div>
                  </div>
                `
              : html`
                  <div class="purchase-receipt-customer-order__row live_chat">
                    판매자 정보를 찾을 수 없습니다. 고객센터로 문의주세요.
                  </div>
                `}
          </div>
          <div class="purchase-receipt-customer-order__info ${typo('16_medium')}">
            <div class="purchase-receipt-customer-order__info-title ${typo('16_bold')}">
              ${this.T.provider_info_t}
            </div>
            ${provider
              ? html`
                  <div class="purchase-receipt-customer-order__row">
                    <div class="purchase-receipt-customer-order__info-left">${this.T.provider_name_t}</div>
                    <div class="purchase-receipt-customer-order__info-right">${provider.name}</div>
                  </div>
                  <div class="purchase-receipt-customer-order__row">
                    <div class="purchase-receipt-customer-order__info-left">${this.T.seller_ceo_t}</div>
                    <div class="purchase-receipt-customer-order__info-right">${provider.ceo}</div>
                  </div>
                  <div class="purchase-receipt-customer-order__row">
                    <div class="purchase-receipt-customer-order__info-left">${this.T.provider_tel_t}</div>
                    <div class="purchase-receipt-customer-order__info-right">${provider.tel}</div>
                  </div>
                  <div class="purchase-receipt-customer-order__row">
                    <div class="purchase-receipt-customer-order__info-left">${this.T.seller_num_t}</div>
                    <div class="purchase-receipt-customer-order__info-right">${provider.num}</div>
                  </div>
                  <div class="purchase-receipt-customer-order__row">
                    <div class="purchase-receipt-customer-order__info-left">${this.T.provider_address_t}</div>
                    <div class="purchase-receipt-customer-order__info-right">${provider.address}</div>
                  </div>
                `
              : html`<div class="purchase-receipt-customer-order__row live_chat">
                  가맹점 정보를 찾을 수 없습니다. 고객센터로 문의주세요.
                </div>`}
          </div>
          <div class="purchase-receipt-customer-order__info ${typo('16_medium')}">
            <div class="purchase-receipt-customer-order__info-title ${typo('16_bold')}">
              ${this.T.price_info_t}
            </div>
            <div class="purchase-receipt-customer-order__row">
              <div class="purchase-receipt-customer-order__info-left">${this.T.total_price_t}</div>
              <div class="purchase-receipt-customer-order__info-right">
                ${formatCurrency(option.price, ET.lang)}
              </div>
            </div>
            ${impt.cancel_amount > 0
              ? html`
                  <div class="purchase-receipt-customer-order__row">
                    <div class="purchase-receipt-customer-order__info-left">${this.T.cancel_price_t}</div>
                    <div
                      class="purchase-receipt-customer-order__info-right purchase-receipt-customer-order__row--red"
                    >
                      -${formatCurrency(cancel_price, ET.lang)}
                    </div>
                  </div>
                `
              : ''}
            <div class="purchase-receipt-customer-order__row">
              <div class="purchase-receipt-customer-order__info-left">${this.T.supply_price_t}</div>
              <div class="purchase-receipt-customer-order__info-right">
                ${formatCurrency(supply_price, ET.lang)}
              </div>
            </div>
            <div class="purchase-receipt-customer-order__row">
              <div class="purchase-receipt-customer-order__info-left">${this.T.vat_price_t}</div>
              <div class="purchase-receipt-customer-order__info-right">
                ${formatCurrency(vat_price, ET.lang)}
              </div>
            </div>
            <div class="purchase-receipt-customer-order__row">
              <div
                class="purchase-receipt-customer-order__info-left purchase-receipt-customer-order__row--blue purchase-receipt-customer-order__result-price ${font(
                  '16_bold',
                )}"
              >
                ${this.T.result_price_t}
              </div>
              <div class="${klass.result_price}  ${font('20_bold')}">
                ${formatCurrency(result_price, ET.lang)}
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  get isMobile(): boolean {
    return !!rune.getSharedData(this)?.is_mobile;
  }

  get T() {
    return {
      cancel_info_t: ET('customer_order::receipt::detail::cancel_info'),
      card_title_t: ET('customer_order::receipt::detail::card_title'),
      cash_title_t: ET('customer_order::receipt::detail::cash_title'),
      seller_info_t: ET('customer_order::receipt::detail::seller_info'),
      seller_name_t: ET('customer_order::receipt::detail::seller_name'),
      seller_ceo_t: ET('customer_order::receipt::detail::seller_ceo'),
      seller_num_t: ET('customer_order::receipt::detail::seller_num'),
      seller_tel_t: ET('customer_order::receipt::detail::seller_tel'),
      seller_address_t: ET('customer_order::receipt::detail::seller_address'),
      provider_info_t: ET('customer_order::receipt::detail::provider_info'),
      provider_name_t: ET('customer_order::receipt::detail::provider_name'),
      // 대표자명
      provider_tel_t: ET('customer_order::receipt::detail::provider_tel'),
      // 사업자등록번호
      provider_address_t: ET('customer_order::receipt::detail::provider_address'),
      price_info_t: ET('customer_order::receipt::price'),
      total_price_t: ET('customer_order::receipt::total_price_t'),
      cancel_price_t: ET('customer_order::receipt::cancel_price_t'),
      supply_price_t: ET('customer_order::receipt::supply_price_t'),
      vat_price_t: ET('customer_order::receipt::vat_price_t'),
      result_price_t: ET('customer_order::receipt::result_price_t'),
      pdf_t: ET('customer_order::receipt::pdf'),
      print_t: ET('customer_order::receipt::print'),
    };
  }

  /**
   * 영수증 타입
   * PURCHASE: 구매 영수증
   * CARD: 카드 영수증
   * CASH: 현금 영수증
   */
  static get type(): typeof ReceiptViewerType {
    return ReceiptViewerType;
  }

  static openReceipt(queryStringObj: {
    type: keyof typeof ReceiptViewerType;
    customer_order_id: string;
    option: ReceiptOption;
  }) {
    const width = ReceiptViewerDefaultSize.width;
    const height = ReceiptViewerDefaultSize.height;
    const left = Math.round(window.screenX + (window.innerWidth - width) / 2);
    const top = Math.round(window.screenY + (window.innerHeight - height) / 2);

    const windowFeatures = `
    width=${width},
    height=${height},
    left=${left},
    top=${top},
    resizable=yes,
    scrollbars=auto,
    status=no,
    menubar=no,
    toolbar=no,
    location=no
  `.replace(/\s/g, '');

    const stringifyObj = {
      type: queryStringObj.type,
      customer_order_id: queryStringObj.customer_order_id,
      option: JSON.stringify(queryStringObj.option),
    };

    const windowName = `printWindow_${Date.now()}`;

    const printWindow = window.open(
      `${receipt_viewer_path}?${makeQueryString(stringifyObj)}`,
      windowName,
      windowFeatures,
    );

    if (printWindow === null) {
      alert('팝업이 차단되어 있습니다. 팝업 차단을 해제해주세요.');
      return null;
    }

    return printWindow;
  }
}
